import { useEffect, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { NavisionStatusDto, Settings, WarehouseDto } from '@thebb/shared';
import { get, patch, post, put } from '../api';
import { useAuth } from '../auth';
import { Badge, ErrorBox, Modal, PageTitle, Spinner, fmtTime } from '../components/ui';

export const warehouseTypeLabel: Record<string, string> = { operated: 'Drives af os', remote: 'Fjernlager' };
const logicLabel: Record<string, string> = { fixed: 'Faste pladser', chaos: 'Kaotisk' };

type Form = { code: string; name: string; type: 'operated' | 'remote' | 'demo'; storageLogic: 'fixed' | 'chaos'; leadTimeDays: string; isDefaultReceiving: boolean; address: { name: string; street1: string; street2: string; zip: string; city: string; countryCode: string; phone: string; email: string } };
const empty: Form = { code: '', name: '', type: 'operated', storageLogic: 'fixed', leadTimeDays: '', isDefaultReceiving: false, address: { name: '', street1: '', street2: '', zip: '', city: '', countryCode: 'DK', phone: '', email: '' } };

function toBody(f: Form) {
  return {
    code: f.code,
    name: f.name,
    type: f.type,
    storageLogic: f.storageLogic,
    leadTimeDays: f.leadTimeDays === '' ? null : Number(f.leadTimeDays),
    isDefaultReceiving: f.isDefaultReceiving,
    address: f.address.street1 || f.address.city ? { ...f.address, street2: f.address.street2 || null, phone: f.address.phone || null, email: f.address.email || null } : null,
  };
}

/** Warehouses: the operated ones pick and ship, remote ones hold callable stock. */
export function WarehousesPage() {
  const qc = useQueryClient();
  const { can } = useAuth();
  const [error, setError] = useState<unknown>(null);
  const [editing, setEditing] = useState<{ id: string | null; form: Form } | null>(null);
  const list = useQuery({ queryKey: ['warehouses'], queryFn: () => get<WarehouseDto[]>('/warehouses') });
  const invalidate = () => void qc.invalidateQueries({ queryKey: ['warehouses'] });
  const save = useMutation({
    mutationFn: (v: { id: string | null; form: Form }) => (v.id ? patch(`/warehouses/${v.id}`, toBody(v.form)) : post('/warehouses', toBody(v.form))),
    onSuccess: () => { invalidate(); setEditing(null); },
    onError: setError,
  });
  const toggle = useMutation({ mutationFn: (w: WarehouseDto) => patch(`/warehouses/${w.id}`, { active: !w.active }), onSuccess: invalidate, onError: setError });

  const openEdit = (w: WarehouseDto) => {
    const a = w.address;
    setEditing({
      id: w.id,
      form: {
        code: w.code,
        name: w.name,
        type: w.type,
        storageLogic: w.storageLogic,
        leadTimeDays: w.leadTimeDays === null ? '' : String(w.leadTimeDays),
        isDefaultReceiving: w.isDefaultReceiving,
        address: { name: a?.name ?? '', street1: a?.street1 ?? '', street2: a?.street2 ?? '', zip: a?.zip ?? '', city: a?.city ?? '', countryCode: a?.countryCode ?? 'DK', phone: a?.phone ?? '', email: a?.email ?? '' },
      },
    });
  };

  return (
    <div>
      <PageTitle title="Lagre" actions={can('settings.manage') && <button className="btn btn-primary" onClick={() => setEditing({ id: null, form: empty })}>Nyt lager</button>} />
      <ErrorBox error={error} />
      {list.isLoading ? (
        <Spinner />
      ) : (
        <table className="table card p-0">
          <thead>
            <tr>
              <th>Kode</th>
              <th>Navn</th>
              <th>Type</th>
              <th>Lagerlogik</th>
              <th>Adresse</th>
              <th className="text-right">Lokationer</th>
              <th>Modtagelse</th>
              <th>Status</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {list.data?.map((w) => (
              <tr key={w.id}>
                <td className="font-medium">{w.code}</td>
                <td>{w.name}</td>
                <td>{warehouseTypeLabel[w.type]}{w.type === 'remote' && w.leadTimeDays !== null && <span className="text-zinc-500"> · {w.leadTimeDays} dage</span>}</td>
                <td>{logicLabel[w.storageLogic]}</td>
                <td className="text-zinc-600">{w.address ? `${w.address.street1}, ${w.address.zip} ${w.address.city}` : '–'}</td>
                <td className="text-right tabular-nums">{w.locationCount}</td>
                <td>{w.isDefaultReceiving && <Badge tone="blue">standard</Badge>}</td>
                <td>{w.active ? <Badge tone="green">aktiv</Badge> : <Badge>lukket</Badge>}</td>
                <td className="whitespace-nowrap text-right">
                  {can('settings.manage') && (
                    <>
                      <button className="btn" onClick={() => openEdit(w)}>Redigér</button>{' '}
                      <button className="btn" onClick={() => toggle.mutate(w)}>{w.active ? 'Luk' : 'Åbn'}</button>
                    </>
                  )}
                </td>
              </tr>
            ))}
            {list.data?.length === 0 && (
              <tr>
                <td colSpan={9} className="text-zinc-500">Ingen lagre endnu. Opret BB, FLP, JP, SP og SØ som i dag.</td>
              </tr>
            )}
          </tbody>
        </table>
      )}

      <NavisionCard onError={setError} />

      {editing && (
        <Modal title={editing.id ? 'Redigér lager' : 'Nyt lager'} onClose={() => setEditing(null)}>
          <WarehouseForm form={editing.form} onChange={(form) => setEditing({ ...editing, form })} />
          <div className="mt-4 flex justify-end gap-2">
            <button className="btn" onClick={() => setEditing(null)}>Annullér</button>
            <button className="btn btn-primary" disabled={save.isPending || !editing.form.code || !editing.form.name} onClick={() => save.mutate(editing)}>Gem</button>
          </div>
        </Modal>
      )}
    </div>
  );
}

/** Navision through the YouWe bridge: status, manual sync, connection test and the settings. */
function NavisionCard({ onError }: { onError: (e: unknown) => void }) {
  const qc = useQueryClient();
  const { can } = useAuth();
  const status = useQuery({ queryKey: ['navision-status'], queryFn: () => get<NavisionStatusDto>('/navision/status'), refetchInterval: 30_000 });
  const settings = useQuery({ queryKey: ['settings'], queryFn: () => get<Settings>('/settings'), enabled: can('settings.manage') });
  const [form, setForm] = useState<Partial<Settings>>({});
  useEffect(() => {
    if (settings.data) {
      const d = settings.data;
      setForm({ 'nav.enabled': d['nav.enabled'], 'nav.mode': d['nav.mode'], 'nav.baseUrl': d['nav.baseUrl'], 'nav.secretKey': d['nav.secretKey'], 'nav.syncHour': d['nav.syncHour'], 'nav.sql.host': d['nav.sql.host'], 'nav.sql.port': d['nav.sql.port'], 'nav.sql.database': d['nav.sql.database'], 'nav.sql.company': d['nav.sql.company'], 'nav.sql.domain': d['nav.sql.domain'], 'nav.sql.user': d['nav.sql.user'], 'nav.sql.password': d['nav.sql.password'], 'nav.applyOrderStatus': d['nav.applyOrderStatus'] });
    }
  }, [settings.data]);
  const invalidate = () => { void qc.invalidateQueries({ queryKey: ['navision-status'] }); void qc.invalidateQueries({ queryKey: ['stock'] }); };
  const save = useMutation({ mutationFn: () => put('/settings', form), onSuccess: () => { void qc.invalidateQueries({ queryKey: ['settings'] }); invalidate(); }, onError });
  const sync = useMutation({ mutationFn: () => post<NavisionStatusDto>('/navision/sync'), onSuccess: invalidate, onError });
  const test = useMutation({ mutationFn: () => post<{ rows: number; columns: string[] }>('/navision/test'), onError });
  const [importResult, setImportResult] = useState<string | null>(null);
  const importLocations = useMutation({
    mutationFn: (v: { warehouseId: string; navLocationCode: string }) => post<{ created: number; updated: number; rejected: unknown[]; source: number }>('/locations/import-navision', { ...v, includeEmpty: false }),
    onSuccess: (r, v) => { setImportResult(`${v.navLocationCode}: ${r.created} nye lokationer, ${r.updated} opdateret, ${r.rejected.length} afvist (af ${r.source} bins i Navision)`); void qc.invalidateQueries({ queryKey: ['locations'] }); void qc.invalidateQueries({ queryKey: ['warehouses'] }); },
    onError,
  });
  const countFromNav = useMutation({
    mutationFn: (v: { warehouseId: string; navLocationCode: string }) => post<{ sessionId: string; lines: number; unknownSkus: string[]; missingLocations: string[] }>('/counts/from-navision', { ...v, name: `Åbningsbalance ${v.navLocationCode} ${new Date().toISOString().slice(0, 10)}` }),
    onSuccess: (r, v) => { setImportResult(`${v.navLocationCode}: optælling oprettet med ${r.lines} linjer; ${r.unknownSkus.length} ukendte varenumre${r.unknownSkus.length ? ` (${r.unknownSkus.slice(0, 10).join(', ')}${r.unknownSkus.length > 10 ? '…' : ''})` : ''}, ${r.missingLocations.length} manglende lokationer${r.missingLocations.length ? ` (${r.missingLocations.slice(0, 10).join(', ')}${r.missingLocations.length > 10 ? '…' : ''})` : ''}. Session ${r.sessionId}`); void qc.invalidateQueries({ queryKey: ['counts'] }); },
    onError,
  });
  const s = status.data;
  return (
    <section className="card mt-6 space-y-3 text-sm">
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="font-semibold">Navision (YouWe-bro)</h2>
        {s && <Badge tone={s.enabled ? (s.keySet ? 'green' : 'amber') : 'zinc'}>{s.enabled ? (s.keySet ? 'aktiv' : s.mode === 'x3' ? 'mangler 3x-token' : 'mangler nøgle') : 'slået fra'}</Badge>}
        {s && <span className="text-zinc-500">{s.mode === 'x3' ? 'via 3x' : 'direkte til broen'} · {s.baseUrl}</span>}
        {s && <span className="text-zinc-600">{s.itemCount} varenumre · hentet {fmtTime(s.lastFetchedAt)}</span>}
        {s?.lastRun && <span className={s.lastRun.ok === false ? 'text-red-700' : 'text-zinc-500'}>seneste kørsel {fmtTime(s.lastRun.startedAt)}: {s.lastRun.ok === null ? 'kører' : s.lastRun.ok ? 'ok' : 'fejl'}{s.lastRun.message && ` · ${s.lastRun.message}`}</span>}
        <span className="ml-auto flex gap-2">
          {can('settings.manage') && <button className="btn" disabled={test.isPending} onClick={() => test.mutate()}>{test.isPending ? 'Tester…' : 'Test forbindelse'}</button>}
          {can('stock.count') && <button className="btn btn-primary" disabled={sync.isPending || !s?.keySet} onClick={() => sync.mutate()}>{sync.isPending ? 'Henter…' : 'Hent fra Navision nu'}</button>}
        </span>
      </div>
      {test.data && <div className="rounded-md border border-green-200 bg-green-50 px-3 py-2 text-green-800">Forbindelse ok: {test.data.rows} rækker · {test.data.columns.join(', ')}</div>}
      {importResult && <div className="rounded-md border border-green-200 bg-green-50 px-3 py-2 text-green-800">{importResult}</div>}
      {s && s.locations.length > 0 && (
        <table className="table">
          <thead><tr><th>Navision-lager</th><th>Navn</th><th className="text-right">Bins</th><th className="text-right">Bins med beholdning</th><th className="text-right">Varenumre</th><th>Vores lager</th><th></th></tr></thead>
          <tbody>
            {s.locations.map((l) => (
              <tr key={l.code}>
                <td className="font-medium">{l.code}</td>
                <td>{l.name}</td>
                <td className="text-right tabular-nums">{l.bins}</td>
                <td className="text-right tabular-nums">{l.binsWithStock}</td>
                <td className="text-right tabular-nums">{l.skus}</td>
                <td>{l.warehouseCode ?? <span className="text-zinc-400">ingen (opret lager med samme kode eller map i nav.locationMap)</span>}</td>
                <td className="whitespace-nowrap text-right">
                  {l.warehouseId && can('stock.locations') && <button className="btn" disabled={importLocations.isPending || l.bins === 0} onClick={() => importLocations.mutate({ warehouseId: l.warehouseId!, navLocationCode: l.code })}>Importér lokationer</button>}{' '}
                  {l.warehouseId && can('stock.count') && <button className="btn" disabled={countFromNav.isPending || l.skus === 0} onClick={() => countFromNav.mutate({ warehouseId: l.warehouseId!, navLocationCode: l.code })}>Åbningsoptælling</button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {s && s.locations.length > 0 && <div className="text-xs text-zinc-500">Rækkefølge: importér lokationer, synk produkter fra 3x, opret åbningsoptælling, gennemgå og bogfør under <Link to="/counts" className="text-blue-700 hover:underline">Optælling</Link>.</div>}
      {can('settings.manage') && settings.data && (
        <div className="grid gap-3 md:grid-cols-4">
          <div>
            <label className="label">Vej til Navision</label>
            <select className="input" value={form['nav.mode'] ?? 'sql'} onChange={(e) => setForm({ ...form, 'nav.mode': e.target.value as 'sql' | 'x3' | 'bridge' })}>
              <option value="sql">Direkte til Navisions SQL Server (via BB-VPN)</option>
              <option value="x3">Via 3x (3x-token; 3x er whitelistet hos broen)</option>
              <option value="bridge">Direkte til YouWe-broen (Secret-Key)</option>
            </select>
          </div>
          <div className="md:col-span-2">
            <label className="label">Base-URL (kun direkte)</label>
            <input className="input" value={form['nav.baseUrl'] ?? ''} disabled={form['nav.mode'] !== 'bridge'} onChange={(e) => setForm({ ...form, 'nav.baseUrl': e.target.value })} />
          </div>
          <div>
            <label className="label">Secret-Key (via 3x: valgfri, erstatter 3x's egen nøgle)</label>
            <input className="input" type="password" autoComplete="off" value={form['nav.secretKey'] ?? ''} onChange={(e) => setForm({ ...form, 'nav.secretKey': e.target.value })} placeholder="indtast for at ændre" />
          </div>
          <div>
            <label className="label">Natlig synk (time)</label>
            <input className="input" type="number" min={0} max={23} value={form['nav.syncHour'] ?? 4} onChange={(e) => setForm({ ...form, 'nav.syncHour': Number(e.target.value) })} />
          </div>
          {form['nav.mode'] === 'sql' && (
            <>
              <div><label className="label">SQL-vært</label><input className="input" value={form['nav.sql.host'] ?? ''} onChange={(e) => setForm({ ...form, 'nav.sql.host': e.target.value })} /></div>
              <div><label className="label">Port</label><input className="input" type="number" value={form['nav.sql.port'] ?? 1433} onChange={(e) => setForm({ ...form, 'nav.sql.port': Number(e.target.value) })} /></div>
              <div><label className="label">Database</label><input className="input" value={form['nav.sql.database'] ?? ''} onChange={(e) => setForm({ ...form, 'nav.sql.database': e.target.value })} /></div>
              <div><label className="label">Firma-præfiks (tabelnavn)</label><input className="input" value={form['nav.sql.company'] ?? ''} onChange={(e) => setForm({ ...form, 'nav.sql.company': e.target.value })} /></div>
              <div><label className="label">Domæne</label><input className="input" value={form['nav.sql.domain'] ?? ''} onChange={(e) => setForm({ ...form, 'nav.sql.domain': e.target.value })} /></div>
              <div><label className="label">Bruger</label><input className="input" autoComplete="off" value={form['nav.sql.user'] ?? ''} onChange={(e) => setForm({ ...form, 'nav.sql.user': e.target.value })} /></div>
              <div><label className="label">Adgangskode</label><input className="input" type="password" autoComplete="new-password" value={form['nav.sql.password'] ?? ''} onChange={(e) => setForm({ ...form, 'nav.sql.password': e.target.value })} placeholder="indtast for at ændre" /></div>
            </>
          )}
          <div className="flex items-center gap-4 md:col-span-3">
            <label className="flex items-center gap-2"><input type="checkbox" checked={!!form['nav.enabled']} onChange={(e) => setForm({ ...form, 'nav.enabled': e.target.checked })} /> Synk slået til</label>
            <label className="flex items-center gap-2" title="Under parallelkørslen: ordrer som Navision har sendt eller faktureret, lukkes også her. Slås fra på skiftedagen."><input type="checkbox" checked={!!form['nav.applyOrderStatus']} onChange={(e) => setForm({ ...form, 'nav.applyOrderStatus': e.target.checked })} /> Luk vores ordrer, når Navision har sendt dem</label>
            <span className="text-xs text-zinc-500">Via 3x bruges 3x-feedets URL og token. Broen selv svarer kun fra whitelistede IP-adresser.</span>
          </div>
          <div className="flex justify-end"><button className="btn btn-primary" disabled={save.isPending} onClick={() => save.mutate()}>Gem</button></div>
        </div>
      )}
    </section>
  );
}

function WarehouseForm({ form, onChange }: { form: Form; onChange: (f: Form) => void }) {
  const set = (p: Partial<Form>) => onChange({ ...form, ...p });
  const setA = (p: Partial<Form['address']>) => onChange({ ...form, address: { ...form.address, ...p } });
  return (
    <div className="grid gap-3 md:grid-cols-2">
      <div>
        <label className="label">Kode</label>
        <input className="input" value={form.code} onChange={(e) => set({ code: e.target.value.toUpperCase() })} placeholder="BB" />
      </div>
      <div>
        <label className="label">Navn</label>
        <input className="input" value={form.name} onChange={(e) => set({ name: e.target.value })} />
      </div>
      <div>
        <label className="label">Type</label>
        <select className="input" value={form.type} onChange={(e) => set({ type: e.target.value as Form['type'] })}>
          <option value="operated">Drives af os (plukker, pakker, sender)</option>
          <option value="remote">Fjernlager (kan kaldes hjem)</option>
        </select>
      </div>
      <div>
        <label className="label">Lagerlogik</label>
        <select className="input" value={form.storageLogic} onChange={(e) => set({ storageLogic: e.target.value as Form['storageLogic'] })}>
          <option value="fixed">Faste pladser (to-trins)</option>
          <option value="chaos">Kaotisk (et-trins)</option>
        </select>
      </div>
      {form.type === 'remote' && (
        <div>
          <label className="label">Leveringstid (dage)</label>
          <input className="input" type="number" min={0} value={form.leadTimeDays} onChange={(e) => set({ leadTimeDays: e.target.value })} />
        </div>
      )}
      <div className="flex items-end pb-2 text-sm">
        <label className="flex items-center gap-2"><input type="checkbox" checked={form.isDefaultReceiving} onChange={(e) => set({ isDefaultReceiving: e.target.checked })} /> Standard for modtagelse</label>
      </div>
      <div className="md:col-span-2 mt-2 text-sm font-semibold">Afsenderadresse (til fragtbookinger)</div>
      <div><label className="label">Firmanavn</label><input className="input" value={form.address.name} onChange={(e) => setA({ name: e.target.value })} /></div>
      <div><label className="label">Gade</label><input className="input" value={form.address.street1} onChange={(e) => setA({ street1: e.target.value })} /></div>
      <div><label className="label">Postnr.</label><input className="input" value={form.address.zip} onChange={(e) => setA({ zip: e.target.value })} /></div>
      <div><label className="label">By</label><input className="input" value={form.address.city} onChange={(e) => setA({ city: e.target.value })} /></div>
      <div><label className="label">Land (ISO)</label><input className="input" maxLength={2} value={form.address.countryCode} onChange={(e) => setA({ countryCode: e.target.value.toUpperCase() })} /></div>
      <div><label className="label">Telefon</label><input className="input" value={form.address.phone} onChange={(e) => setA({ phone: e.target.value })} /></div>
      <div className="md:col-span-2"><label className="label">E-mail</label><input className="input" value={form.address.email} onChange={(e) => setA({ email: e.target.value })} /></div>
    </div>
  );
}
